import { useState, useEffect, useRef } from "react";
import "./NumberSequenceGame.css"; // We'll style it separately

export default function NumberSequenceGame() {
  const [level, setLevel] = useState("Easy");
  const [sequence, setSequence] = useState([]);
  const [nextNumber, setNextNumber] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(
    parseInt(localStorage.getItem("numberSeqHighScore") || "0")
  );
  const [streak, setStreak] = useState(0);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState("Classic");
  const [timerLength, setTimerLength] = useState(60);
  const [timeLeft, setTimeLeft] = useState(timerLength);
  const [gameActive, setGameActive] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [theme, setTheme] = useState("neon");

  const timerRef = useRef(null);

  // Generate math sequence
  const generateSequence = () => {
    const start = Math.floor(Math.random() * 10) + 1;
    let seq = [];
    let next = 0;
    const types = {
      Easy: ["arithmetic"],
      Medium: ["arithmetic", "geometric"],
      Hard: ["fibonacci", "geometric"],
      Challenge: ["fibonacci", "arithmetic", "square", "mixed"],
    };
    const chosenType =
      types[level][Math.floor(Math.random() * types[level].length)];

    switch (chosenType) {
      case "arithmetic": {
        const diff = Math.floor(Math.random() * 5) + 1;
        seq = [start, start + diff, start + 2 * diff];
        next = start + 3 * diff;
        break;
      }
      case "geometric": {
        const ratio = Math.floor(Math.random() * 3) + 2;
        seq = [start, start * ratio, start * ratio * ratio];
        next = start * ratio * ratio * ratio;
        break;
      }
      case "fibonacci": {
        const second = start + Math.floor(Math.random() * 3);
        seq = [start, second, start + second];
        next = second + (start + second);
        break;
      }
      case "square": {
        seq = [start, start ** 2, (start + 1) ** 2];
        next = (start + 2) ** 2;
        break;
      }
      case "mixed": {
        const a = start;
        const b = start + Math.floor(Math.random() * 5) + 1;
        seq = [a, b, a + b];
        next = a + b + b;
        break;
      }
      default: {
        seq = [start, start + 1, start + 2];
        next = start + 3;
      }
    }

    // Increase complexity if streak >=3
    if (streak >= 3) {
      const extra = Math.floor(Math.random() * 5) + 1;
      seq = seq.map((n, i) => n + i * extra);
      next += 3 * extra;
    }

    setSequence(seq);
    setNextNumber(next);
  };

  // Timer logic
  useEffect(() => {
    if (!gameActive || mode !== "Timer") return;

    if (timeLeft <= 0) {
      setGameActive(false);
      setMessage(`⏰ Time's up! Final Score: ${score}`);
      clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => setTimeLeft((t) => t - 1), 1000);

    return () => clearInterval(timerRef.current);
  }, [gameActive, mode, timeLeft]);

  useEffect(() => {
    generateSequence();
  }, [level, streak]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!gameActive && mode === "Timer") setGameActive(true);

    if (parseInt(userAnswer) === nextNumber) {
      let points = 10 + Math.floor(streak / 3) * 5;
      if (level === "Medium") points += 5;
      if (level === "Hard") points += 10;
      if (level === "Challenge") points += 20;
      if (hintUsed) points = Math.floor(points / 2);

      setScore(score + points);
      setStreak(streak + 1);
      setMessage(`✅ Correct! +${points} points (Streak x${streak + 1})`);
      if (score + points > highScore) {
        setHighScore(score + points);
        localStorage.setItem("numberSeqHighScore", score + points);
      }
    } else {
      setScore(Math.max(0, score - 5));
      setStreak(0);
      setMessage(`❌ Wrong! Correct answer was ${nextNumber}`);
    }

    setHintUsed(false);
    setHistory([ { seq: sequence, next: nextNumber }, ...history.slice(0, 9)]);
    setUserAnswer("");
    generateSequence();
  };

  const handleRestart = () => {
    setScore(0);
    setStreak(0);
    setMessage("");
    setHistory([]);
    setTimeLeft(timerLength);
    setGameActive(false);
    setHintUsed(false);
    generateSequence();
  };

  const handleHint = () => {
    setUserAnswer(nextNumber);
    setHintUsed(true);
    setMessage("❗ Hint used! Points halved this round.");
  };

  const timerPercent = (timeLeft / timerLength) * 100;
  const wrapperStyle =
    theme === "neon"
      ? { color: "#fff", textShadow: "0 0 10px #ff00ff, 0 0 20px #ff66ff" }
      : { color: "#fff" };

  return (
    <div className="number-seq-wrapper" style={wrapperStyle}>
      <h1>Number Sequence Challenge</h1>
      <p>Level: {level} | Score: {score} | High Score: {highScore} | Streak: x{streak}</p>

      {/* Mode & Timer */}
      <div className="controls">
        <label>Mode: </label>
        <select value={mode} onChange={(e) => setMode(e.target.value)}>
          <option>Classic</option>
          <option>Timer</option>
        </select>

        {mode === "Timer" && (
          <>
            <label style={{ marginLeft: "10px" }}>Timer Length: </label>
            <select
              value={timerLength}
              onChange={(e) => {
                setTimerLength(Number(e.target.value));
                setTimeLeft(Number(e.target.value));
              }}
            >
              <option value={30}>30s</option>
              <option value={60}>60s</option>
              <option value={120}>120s</option>
            </select>
            <div className="timer-bar">
              <div className="timer-fill" style={{ width: `${timerPercent}%` }}></div>
            </div>
            <p>⏳ Time Left: {timeLeft}s</p>
          </>
        )}
      </div>

      {/* Sequence Display */}
      <div className="sequence-display">{sequence.join(", ")} , ?</div>

      <form onSubmit={handleSubmit} className="answer-form">
        <input
          type="number"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          placeholder="Enter next number"
          required
          disabled={mode === "Timer" && timeLeft <= 0}
        />
        <button type="submit" disabled={mode === "Timer" && timeLeft <= 0}>Submit</button>
        <button type="button" onClick={handleHint} disabled={hintUsed || (mode === "Timer" && timeLeft <= 0)}>Hint</button>
      </form>

      {message && (
        <p className={`message ${message.includes("Correct") ? "correct" : "wrong"}`}>{message}</p>
      )}

      <div className="level-theme">
        <label>Level: </label>
        <select value={level} onChange={(e) => setLevel(e.target.value)}>
          <option>Easy</option>
          <option>Medium</option>
          <option>Hard</option>
          <option>Challenge</option>
        </select>

        <label style={{ marginLeft: "10px" }}>Theme: </label>
        <select value={theme} onChange={(e) => setTheme(e.target.value)}>
          <option value="neon">Neon</option>
          <option value="dark">Dark</option>
        </select>
      </div>

      <button onClick={handleRestart} className="restart-btn">Restart</button>

      {history.length > 0 && (
        <div className="history">
          <h4>Last Sequences:</h4>
          <ul>
            {history.map((h, idx) => (
              <li key={idx}>{h.seq.join(", ")} → {h.next}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="adsense-text">
        Improve your mental math skills with this number sequence challenge! Solve sequences accurately and quickly to maximize your score.
      </p>
    </div>
  );
}
